import { useState } from "react";
import { LogOut, Trash2, Pencil, Car, Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { departVisitor, deleteVisitor, updateVisitor } from "@/lib/api";
import { fmtTime, isOverstay, durationLabel, motifLabel } from "@/lib/format";
import { useAuth } from "@/hooks/useAuth";

const StatusBadge = ({ v, nowMs }) => {
  if (v.heure_depart) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-2.5 py-1 text-xs font-semibold" data-testid="status-parti">
        Parti
      </span>
    );
  }
  if (isOverstay(v, nowMs)) {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 px-2.5 py-1 text-xs font-bold animate-pulse-subtle" data-testid="status-overstay">
        Dépassé &gt;4h
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1 text-xs font-semibold" data-testid="status-present">
      Présent
    </span>
  );
};

export const RegisterTable = ({ visitors, nowMs, onChanged }) => {
  const { isAdmin } = useAuth();
  const [departing, setDeparting] = useState(null);
  const [toDepart, setToDepart] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleDepart = async (v) => {
    setToDepart(null);
    setDeparting(v.id);
    try {
      await departVisitor(v.id);
      toast.success(`Départ pointé : ${v.nom}`);
      onChanged();
    } catch {
      toast.error("Erreur lors du pointage.");
    } finally {
      setDeparting(null);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteVisitor(toDelete.id);
      toast.success("Entrée supprimée.");
      onChanged();
    } catch {
      toast.error("Suppression impossible.");
    } finally {
      setToDelete(null);
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await updateVisitor(editing.id, {
        nom: editing.nom,
        societe: editing.societe,
        telephone: editing.telephone,
        personne_visitee: editing.personne_visitee,
        service: editing.service,
        badge: editing.badge,
        piece_identite: editing.piece_identite,
        plaque: editing.plaque,
      });
      toast.success("Entrée modifiée.");
      setEditing(null);
      onChanged();
    } catch {
      toast.error("Modification impossible.");
    } finally {
      setSaving(false);
    }
  };

  if (!visitors.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400" data-testid="empty-register">
        <Inbox className="h-12 w-12 mb-3" />
        <p className="text-base font-medium">Aucun visiteur pour ce filtre.</p>
        <p className="text-sm">Enregistrez une arrivée pour commencer.</p>
      </div>
    );
  }

  return (
    <>
      {/* Vue cartes — mobile uniquement */}
      <div className="lg:hidden space-y-3" data-testid="register-cards">
        {visitors.map((v) => {
          const over = !v.heure_depart && isOverstay(v, nowMs);
          return (
            <div
              key={v.id}
              className={`rounded-xl border shadow-sm p-4 ${over ? "border-red-300 bg-red-50/70" : "border-slate-200 bg-white"}`}
              data-testid={`visitor-card-${v.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {v.badge && (
                      <span className="font-mono-data text-xs font-semibold bg-[#F7EBAA] text-[#8B6B1B] px-2 py-0.5 rounded">
                        {v.badge}
                      </span>
                    )}
                    <StatusBadge v={v} nowMs={nowMs} />
                  </div>
                  <div className="font-semibold text-slate-900 mt-1.5 text-base truncate">{v.nom}</div>
                  {v.societe && <div className="text-xs text-slate-500 truncate">{v.societe}</div>}
                  {v.telephone && <div className="text-xs text-slate-400 font-mono-data">{v.telephone}</div>}
                </div>
                <div className="text-right text-xs text-slate-500 shrink-0 space-y-0.5">
                  <div>Arr. <span className="font-mono-data text-slate-700">{fmtTime(v.heure_arrivee)}</span></div>
                  <div>Dép. <span className="font-mono-data text-slate-700">{fmtTime(v.heure_depart)}</span></div>
                  <div>Durée <span className="font-mono-data text-slate-700">{durationLabel(v, nowMs)}</span></div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div className="min-w-0">
                  <span className="text-[11px] uppercase tracking-wide text-slate-400 block">Personne visitée</span>
                  <span className="text-slate-800">{v.personne_visitee}</span>
                  {v.service && <span className="text-slate-500 block text-xs">{v.service}</span>}
                </div>
                <div className="min-w-0">
                  <span className="text-[11px] uppercase tracking-wide text-slate-400 block">Motif</span>
                  <span className="text-slate-800">{motifLabel(v)}</span>
                  {v.plaque && <span className="text-slate-500 block text-xs font-mono-data uppercase">{v.plaque}</span>}
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                {!v.heure_depart && (
                  <Button
                    onClick={() => setToDepart(v)}
                    disabled={departing === v.id}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-11 gap-1.5 font-semibold"
                    data-testid={`depart-btn-m-${v.id}`}
                  >
                    {departing === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                    Pointer départ
                  </Button>
                )}
                {isAdmin && (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-11 w-11 border-slate-300 text-slate-600"
                      onClick={() => setEditing({ ...v })}
                      data-testid={`edit-btn-m-${v.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-11 w-11 border-slate-300 text-red-600"
                      onClick={() => setToDelete(v)}
                      data-testid={`delete-btn-m-${v.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Vue tableau — tablette / ordinateur */}
      <div className="hidden lg:block overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-md">
        <Table data-testid="register-table">
          <TableHeader>
            <TableRow className="bg-[#0A192F] hover:bg-[#0A192F]">
              {["Badge", "Visiteur", "Personne visitée", "Motif / Véhicule", "Arrivée", "Départ", "Durée", "Statut", "Actions"].map((h) => (
                <TableHead key={h} className="text-[#D4AF37] font-serif-title text-xs uppercase tracking-wider whitespace-nowrap">
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visitors.map((v) => (
              <TableRow
                key={v.id}
                className={`transition-colors ${!v.heure_depart && isOverstay(v, nowMs) ? "bg-red-50/70 hover:bg-red-50" : "hover:bg-slate-50"}`}
                data-testid={`visitor-row-${v.id}`}
              >
                <TableCell>
                  {v.badge ? (
                    <span className="font-mono-data text-xs font-semibold bg-[#F7EBAA] text-[#8B6B1B] px-2 py-1 rounded">
                      {v.badge}
                    </span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-semibold text-slate-900">{v.nom}</div>
                  {v.societe && <div className="text-xs text-slate-500">{v.societe}</div>}
                  {v.telephone && <div className="text-xs text-slate-400 font-mono-data">{v.telephone}</div>}
                </TableCell>
                <TableCell className="text-slate-700">
                  <div>{v.personne_visitee}</div>
                  {v.service && <div className="text-xs text-slate-500">{v.service}</div>}
                </TableCell>
                <TableCell>
                  <span className="inline-block text-xs bg-slate-100 text-slate-700 rounded px-2 py-0.5 font-medium">
                    {motifLabel(v)}
                  </span>
                  {v.plaque && (
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-1 font-mono-data uppercase">
                      <Car className="h-3 w-3" /> {v.plaque}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-mono-data text-sm text-slate-700 whitespace-nowrap">{fmtTime(v.heure_arrivee)}</TableCell>
                <TableCell className="font-mono-data text-sm text-slate-700 whitespace-nowrap">{fmtTime(v.heure_depart)}</TableCell>
                <TableCell className="font-mono-data text-sm text-slate-600 whitespace-nowrap">{durationLabel(v, nowMs)}</TableCell>
                <TableCell><StatusBadge v={v} nowMs={nowMs} /></TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    {!v.heure_depart && (
                      <Button
                        size="sm"
                        onClick={() => setToDepart(v)}
                        disabled={departing === v.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-4 gap-1.5 font-semibold text-sm"
                        data-testid={`depart-btn-${v.id}`}
                      >
                        {departing === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                        <span className="hidden xl:inline">Pointer départ</span>
                      </Button>
                    )}
                    {isAdmin && (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-10 w-10 text-slate-500 hover:text-[#0A192F]"
                          onClick={() => setEditing({ ...v })}
                          data-testid={`edit-btn-${v.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-10 w-10 text-slate-400 hover:text-red-600"
                          onClick={() => setToDelete(v)}
                          data-testid={`delete-btn-${v.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!toDepart} onOpenChange={(o) => !o && setToDepart(null)}>
        <AlertDialogContent data-testid="depart-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Pointer le départ ?</AlertDialogTitle>
            <AlertDialogDescription>
              Confirmez le départ de <b>{toDepart?.nom}</b>. L'heure de départ sera horodatée maintenant
              {" "}({new Date().toLocaleTimeString("fr-FR")}).
              <span className="block mt-3 font-semibold text-[#0A192F]">
                Avez-vous récupéré le badge remis et rendu la pièce d'identité du client&nbsp;?
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-depart-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDepart(toDepart)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-depart-btn"
            >
              Confirmer le départ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette entrée ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'enregistrement de <b>{toDelete?.nom}</b> sera définitivement supprimé du registre.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-title">Modifier l'entrée</DialogTitle>
            <DialogDescription>
              Mettez à jour les informations du visiteur puis enregistrez.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["nom", "Nom complet"],
                ["societe", "Société"],
                ["telephone", "Téléphone"],
                ["personne_visitee", "Personne à visiter"],
                ["service", "Service"],
                ["badge", "Badge"],
                ["piece_identite", "Pièce d'identité"],
                ["plaque", "Plaque"],
              ].map(([k, l]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs uppercase text-slate-500">{l}</Label>
                  <Input
                    value={editing[k] || ""}
                    onChange={(e) => setEditing((s) => ({ ...s, [k]: e.target.value }))}
                    data-testid={`edit-input-${k}`}
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-[#0A192F] hover:bg-[#1E293B]" data-testid="save-edit-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
